import * as React from "react";
import { Button, Modal, Typography, Card, Box, Grid, Divider, TextField, IconButton, Icon, useMediaQuery } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { format } from "date-fns";
import ToolTipIcon from "../../../icons/toolTipQuestionMark.svg";
import BackIcon from "../../../icons/backIcon.svg";
import CancelIcon from "../../../icons/cancelIcon.svg";
import SpinnerModal from "../../../shared/Modal/SpinnerModal";

const useStyles = makeStyles({
  modalContainer: {
    background: "#FFFFFF",
    boxShadow: "none",
    borderRadius: "24px",
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "33px",
    justifyContent: "flex-end",
    alignItems: "center",
    margin: "0 auto",
    marginTop: "30px",
  },
  reviewAndSubmitMainDetails: {
    marginBottom: "18px",
  },
});

interface refundActionConfirmationModalProps {
  open: boolean;
  paymentID: string;
  transactionDate: string | undefined;
  pmCompany: string;
  leaseHolderName: string;
  properyName: string;
  unitName:string;
  isActionSuccess: boolean;
  actionError: string | null;
  isActionError: boolean;
  actionMessage: string;
  closeCallback: (event: object, reason: string) => void;
  closeBaseModal: (event: object, reason: string) => void;
  setGlobalError: (value: React.SetStateAction<string | null>) => void;
  handleConfirmationButtonClicked: (transactionUuid: string, message: string) => void;
}

export default function RefundActionConfirmationModal(props: refundActionConfirmationModalProps) {
  const classes = useStyles();
  const isOnMobileScreen = useMediaQuery("(max-width:600px)");
  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    padding: "16px",
    height: "585px",
    width: "520px",
  };
  
  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, []);

  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const [resonForAction, setReasonForAction] = React.useState("");
  const [errorMessage , setErrorMessage] = React.useState<string>()

  React.useEffect(() => {
    if (props.isActionSuccess) {
      setSpinnerStatus("stop");
    }
  }, [props.isActionSuccess]);

  React.useEffect(() => {
    if (props.isActionError) {
      handleCloseSpinnerModal();
      props.setGlobalError(props.actionError);
    }
  }, [props.isActionError, props.actionError, props.setGlobalError, handleCloseSpinnerModal]);

  const handleConfirmation = () => {
    if(!resonForAction.length){
      setErrorMessage('Enter a message to the payer.')
    }else {
      props.handleConfirmationButtonClicked(props.paymentID, resonForAction);
      setIsSpinnerModalOpen(true);
    }
  };

  const handleCloseDenyModal = () => {
    props.closeCallback({}, "closeDetailsModal");
  };

  const handleReasonChange = (e : React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const { value } = e.target;
    setReasonForAction(value);
    setErrorMessage(value.length ? undefined : 'Enter a message to the payer.');
  }

  return (
    <div className={classes.modalContainer}>
      <Modal open={props.open} hideBackdrop={true} onClose={handleCloseDenyModal} aria-labelledby="simple-modal-title" aria-describedby="simple-modal-description">
        <Card sx={style}>
          <>
            {/* Deny Refund Spinner */}
            {isSpinnerModalOpen ? (
              <SpinnerModal width="520px" height={isOnMobileScreen ? "100%" : "555px"} openSpinnerModal={isSpinnerModalOpen} handleCloseSpinnerModal={handleCloseSpinnerModal} spinnerStatus={spinnerStatus} closeCancel={() => props.closeCallback({}, "closeThankYouModal")} />
            ) : (
              <></>
            )}

            <Grid container>
              <Grid item xs={2}>
                <IconButton onClick={handleCloseDenyModal}>
                  <Icon>
                    <img src={BackIcon} alt="" />
                  </Icon>
                </IconButton>
              </Grid>
              <Grid item xs={8}>
                <Typography id="payment-id" sx={{ mt: 2 }}>
                  Payment ID: {props.paymentID}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <IconButton sx={{ ml: "30px" }} onClick={handleCloseDenyModal}>
                  <Icon>
                    <img src={CancelIcon} alt="" width={24} height={24} />
                  </Icon>
                </IconButton>
              </Grid>
            </Grid>
            <Typography id="payment-details-date">{!!props.transactionDate && format(new Date(props.transactionDate), "PPPPpp")}</Typography>
            <Box sx={{ width: "100%" }}>
              <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                <Grid item xs={6}>
                  <Typography
                    style={{
                      color: "#14142B",
                      fontSize: "16px",
                    }}
                    id="review-submit-main-details"
                  >
                    {props.pmCompany}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    style={{
                      color: "#14142B",
                      fontSize: "16px",
                    }}
                    id="review-submit-main-details"
                  >
                    {props.properyName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <div className={classes.reviewAndSubmitMainDetails}>
                    <Typography
                      style={{
                        color: "#14142B",
                        fontSize: "16px",
                      }}
                      id="review-submit-main-details"
                    >
                      {props.leaseHolderName}
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6}>
                  <div className={classes.reviewAndSubmitMainDetails}>
                    <Typography
                      style={{
                        color: "#14142B",
                        fontSize: "16px",
                      }}
                      id="review-submit-main-details"
                    >
                      {props.unitName}
                    </Typography>
                  </div>
                </Grid>
              </Grid>
              <Divider />
              {/* 
                rest goes here
              */}
              <Typography sx={{ mt: "21px", mb: "10px", fontWeight: "600" }}>
                Your message &nbsp;
                <img src={ToolTipIcon} alt="" title="explain to the resident why the request has been denied" />
              </Typography>
              <Box
                sx={{
                  "& fieldset": {
                    borderRadius: "24px",
                  },
                }}
              >
                <TextField
                  error={!!errorMessage} 
                  multiline
                  value={resonForAction}
                  onChange={handleReasonChange}
                  rows={8}
                  sx={{
                    width: "100%",
                    backgroundColor: "#F1F0F5",
                    borderRadius: "24px",
                  }}
                />
                {errorMessage && <Typography sx={{color: '#ff0000', fontSize:'14px'}}>{errorMessage}</Typography>}
              </Box>
              <Grid container>
                <Grid item xs={8}></Grid>
                <Grid item xs={4}>
                  <div className={classes.buttonContainer}>
                    <Button
                      variant="contained"
                      id="done"
                      style={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: "normal",
                        fontWeight: 700,
                        background:!resonForAction.length ? "#B0B0B0" : "#39A2DB",
                        color: "white",
                      }}
                      onClick={handleConfirmation}
                    >
                      {props.actionMessage}
                    </Button>
                  </div>
                </Grid>
              </Grid>
            </Box>
          </>
        </Card>
      </Modal>
    </div>
  );
}
